import { Contact } from "@deathstar/types/northstar";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React from "react";
import { useWatch } from "react-hook-form";
import { classNames } from "../../classNames/classNames";
import type { Form } from "./BillingSettingsDialog";

export function BillingContactSelector({ form, contacts, className, readonly, disabled }: BillingContactSelector.Props): JSX.Element {
    const contactIds = useWatch({
        control: form.control,
        name: "contactIds",
    });
    const fieldError = form.getFieldState("contactIds")?.error;

    const validContacts = React.useMemo(() => {
        return contacts?.filter((contact) => contact.email) || [];
    }, [contacts]);

    const selectedContacts = (contactIds || []).map((id) => validContacts.find((contact) => contact.id === id)).filter(Boolean);

    if (readonly) {
        return (
            <TextField
                variant="outlined"
                size="small"
                className={classNames(className, "lg:min-w-[300px]", {
                    "pointer-events-none": readonly || disabled,
                })}
                disabled={disabled}
                error={Boolean(fieldError)}
                helperText={fieldError ? fieldError.message : null}
                label="Billing Contacts"
                value={selectedContacts.map(getOptionLabel).join(", ")}
            />
        );
    }

    return (
        <Autocomplete<Contact, true, false, false>
            multiple
            filterSelectedOptions
            disabled={disabled}
            size="small"
            className={classNames(className, "lg:min-w-[300px]", {
                "pointer-events-none": readonly || disabled,
            })}
            options={validContacts}
            value={selectedContacts}
            getOptionLabel={(option) => getOptionLabel(option)}
            getOptionSelected={(option, value) => option.id === value?.id}
            onChange={(e, option, _reason) => {
                e.stopPropagation();
                e.preventDefault();
                form.setValue(
                    "contactIds",
                    option.map((contact) => contact.id),
                    { shouldDirty: true }
                );
                form.setValue("contactId", option[0]?.id ?? null);
                form.clearErrors("contactIds");
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label="Billing Contacts"
                    error={Boolean(fieldError)}
                    helperText={fieldError?.message || selectedContacts.map((contact) => contact.email).join(", ")}
                    inputProps={{
                        ...params.inputProps,
                        "data-lpignore": "true",
                    }}
                    // readonly={readonly}
                    disabled={disabled}
                />
            )}
        />
    );
}

export namespace BillingContactSelector {
    export interface Props {
        form: Form;
        contacts?: Contact[];
        className?: string;
        readonly?: boolean;
        disabled?: boolean;
    }
}

function getOptionLabel(option: Contact): string {
    const parts: string[] = [];
    if (option?.firstName) {
        parts.push(option.firstName);
    }
    if (option?.lastName) {
        parts.push(option.lastName);
    }
    return parts.join(" ") || option?.email || "";
}
